import { MovieClip, Sound, SoundChannel, SoundTransform } from "../runtime";
import {
  MUSIC,
  MUSIC_TRACKS,
  SOUND_EFFECTS,
  SOUND_STATES,
  SOUND_VARIANTS,
  type MusicName,
  type SoundClass,
  type SoundEffectName,
  type SoundVariantName,
} from "./data/sounds";

/** Plays the game's sound effects and music off the tables in `data/sounds`. */
export class SoundFX extends MovieClip {
  private soundOn = true;
  private musOn = true;

  constructor() {
    super();
    this.addEventListener("enterFrame", this.update);
  }

  kill(): void {
    this.removeEventListener("enterFrame", this.update);
    this.chaMus?.stop();
    this.chaMus = null;
    this.chaLooping?.stop();
    this.chaLooping = null;
  }

  musicStart(command: number = 0, resume: boolean = false): void {
    if (this.currentTrack !== command || resume) {
      this.currentTrack = command;
      if (this.musOn) {
        const track = MUSIC_TRACKS[this.currentTrack];
        if (track) this.playMusic(track);
        this.volCurrent = 1;
        this.volTarget = 1;
        this.transformMus.volume = this.volCurrent;
        // unknown track numbers still poke the previous channel, as in the original
        this.chaMus!.soundTransform = this.transformMus;
      }
    }
  }

  musicResume(): void {
    if (this.musOn && this.currentTrack !== 0)
      this.musicStart(this.currentTrack, true);
  }

  musicStop(command: string = "stop"): void {
    if (this.currentTrack !== 0) {
      if (command === "stop") this.currentTrack = 0;
      this.volCurrent = 0;
      this.transformMus.volume = this.volCurrent;
      this.chaMus!.soundTransform = this.transformMus;
      this.chaMus!.stop();
    }
  }

  musicFadeOut(fadeSpeed: number): void {
    if (this.musOn) {
      this.fadeSpeedMus = fadeSpeed;
      this.volTarget = 0;
      if (this.chaMus == null) this.volCurrent = 0;
    }
  }

  updateSoundState(soundState: number = 1): void {
    const state = SOUND_STATES[soundState];
    if (state) [this.soundOn, this.musOn] = state;
  }

  cleanserLoop(mode: number = 0): void {
    if (this.soundOn) {
      if (mode === 0) this.chaLooping = this.fx.cleanserLoop.play(0, 99999);
      if (mode === 1 && this.chaLooping != null) this.chaLooping.stop();
    }
  }

  cleanserVolume(newVolume: number = 0): void {
    if (this.soundOn) {
      this.transformLooping.volume = newVolume;
      this.chaLooping!.soundTransform = this.transformLooping;
    }
  }

  private update = (): void => {
    if (this.musOn) {
      if (this.volCurrent > this.volTarget) {
        this.volCurrent -= this.fadeSpeedMus;
        this.transformMus.volume = this.volCurrent;
        this.chaMus!.soundTransform = this.transformMus;
      }
      if (this.volCurrent <= 0) {
        this.volCurrent = 0;
        this.transformMus.volume = this.volCurrent;
        this.chaMus!.soundTransform = this.transformMus;
        this.chaMus!.stop();
      }
    }
  };

  menuChange1 = (): void => this.playFx("menuChange1");
  menuChange2 = (): void => this.playFx("menuChange2");
  menuSelect1 = (): void => this.playFx("menuSelect1");
  thunder = (): void => this.playFx("thunder");
  openingComputer = (): void => this.playFx("openingComputer");
  openingBoost = (): void => this.playFx("openingBoost");
  mine1 = (): void => this.playFx("mine1");
  mine2 = (): void => this.playFx("mine2");
  enemySlash = (): void => this.playFx("enemySlash");
  enemyShotOrb = (): void => this.playFx("enemyShotOrb");
  enemyShotSickle = (): void => this.playFx("enemyShotSickle");
  enemyShotLaser = (): void => this.playFx("enemyShotLaser");
  missileLaunch = (): void => this.playFx("missileLaunch");
  explodeBig = (): void => this.playFx("explodeBig");
  explodeHuge = (): void => this.playFx("explodeHuge");
  explodeGargantuan = (): void => this.playFx("explodeGargantuan");
  item1 = (): void => this.playFx("item1");
  item2 = (): void => this.playFx("item2");
  item3 = (): void => this.playFx("item3");
  effect1 = (): void => this.playFx("effect1");
  effect2 = (): void => this.playFx("effect2");
  effect3 = (): void => this.playFx("effect3");
  dash = (): void => this.playFx("dash");
  weaponChange = (): void => this.playFx("weaponChange");
  shine = (): void => this.playFx("shine");
  appear = (): void => this.playFx("appear");
  death = (): void => this.playFx("death");
  doors = (): void => this.playFx("doors");
  damage = (): void => this.playFx("damage");
  scytheBlackHole = (): void => this.playFx("scytheBlackHole");
  scytheShot = (): void => this.playFx("scytheShot");
  staffCatch = (): void => this.playFx("staffCatch");
  staffSpin = (): void => this.playFx("staffSpin");
  staffThrow = (): void => this.playFx("staffThrow");
  textBoxAppear = (): void => this.playFx("textBoxAppear");
  axeCharge = (): void => this.playFx("axeCharge");
  axeShockwave = (): void => this.playFx("axeShockwave");
  swordSpin = (): void => this.playFx("swordSpin");
  missionBar1 = (): void => this.playFx("missionBar1");
  missionBar2 = (): void => this.playFx("missionBar2");

  hitEnemy = (): void => this.playVariant("hitEnemy");
  hitEnemySpecial = (): void => this.playVariant("hitEnemySpecial");
  explodeEnemy = (): void => this.playVariant("explodeEnemy");
  swipe = (): void => this.playVariant("swipe");
  swipeB = (): void => this.playVariant("swipeB");
  swipeC = (): void => this.playVariant("swipeC");
  swipeD = (): void => this.playVariant("swipeD");
  swipeE = (): void => this.playVariant("swipeE");

  private currentTrack = 0;
  private volCurrent = 1;
  private volTarget = 1;
  private fadeSpeedMus = 0.1;
  private readonly transformMus = new SoundTransform();
  private readonly transformLooping = new SoundTransform();
  private chaLooping: SoundChannel | null = null;
  private chaMus: SoundChannel | null = null;
  private readonly fx = instantiate(SOUND_EFFECTS);
  private readonly variants = instantiateVariants(SOUND_VARIANTS);

  private playFx(name: SoundEffectName): void {
    if (this.soundOn) this.fx[name].play();
  }

  private playVariant(name: SoundVariantName): void {
    if (this.soundOn) {
      // ceil() yields 0 if random() returns exactly 0; the original played nothing then
      const pick = this.variants[name][Math.ceil(Math.random() * 3) - 1];
      if (pick) pick.play();
    }
  }

  private playMusic(name: MusicName): void {
    this.chaMus?.stop();
    const track = MUSIC[name];
    this.chaMus = new track.sound().play(0, track.loops);
  }
}

function instantiate<T extends Record<string, SoundClass>>(
  table: T,
): { readonly [K in keyof T]: Sound } {
  const made = Object.entries(table).map(([key, cls]) => [key, new cls()]);
  return Object.fromEntries(made) as { readonly [K in keyof T]: Sound };
}

function instantiateVariants<T extends Record<string, readonly SoundClass[]>>(
  table: T,
): { readonly [K in keyof T]: readonly Sound[] } {
  const made = Object.entries(table).map(([key, list]) => [
    key,
    list.map((cls) => new cls()),
  ]);
  return Object.fromEntries(made) as {
    readonly [K in keyof T]: readonly Sound[];
  };
}
